// Any picture at any size. Fit, fill and 1:1, pan by dragging, zoom with the
// wheel or the keys, rotate and flip as view transforms only. SVG goes through
// an <img>, which never runs scripts. Copy and Export draw the transformed view
// onto a canvas and never touch the original.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react';
import { SegmentedControl } from '../../inputs/SegmentedControl/SegmentedControl.js';
import { downloadBlob } from '@repletoryreach/sdk-utils/file';
import { IconButton, Toolbar, ToolbarGroup, ToolbarText } from '../chrome.js';
import { IconChecker, IconFlipH, IconFlipV, IconReset, IconRotateLeft, IconRotateRight, IconZoomIn, IconZoomOut } from '../icons.js';
import { formatAspect } from '../format.js';
import type { RendererProps } from '../types.js';

type Mode = 'fit' | 'fill' | 'actual' | 'custom';

const ZOOM_STEP = 1.25;
const MIN_SCALE = 0.02;
const MAX_SCALE = 32;

export function ImageRenderer({ file, detected, labels, register, announce, reducedMotion }: RendererProps): ReactNode {
  const src = file.objectUrl ?? file.streamUrl ?? '';
  const box = useRef<HTMLDivElement | null>(null);
  const img = useRef<HTMLImageElement | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [boxSize, setBoxSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [mode, setMode] = useState<Mode>('fit');
  const [custom, setCustom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipX, setFlipX] = useState(false);
  const [flipY, setFlipY] = useState(false);
  const [checker, setChecker] = useState(true);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [failed, setFailed] = useState(false);
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setBoxSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  const rotated = rotation % 180 !== 0;
  const fitScale = useMemo(() => {
    if (!natural || !boxSize.w || !boxSize.h) return 1;
    const w = rotated ? natural.h : natural.w;
    const h = rotated ? natural.w : natural.h;
    return Math.min((boxSize.w - 32) / w, (boxSize.h - 32) / h);
  }, [natural, boxSize, rotated]);
  const fillScale = useMemo(() => {
    if (!natural || !boxSize.w || !boxSize.h) return 1;
    const w = rotated ? natural.h : natural.w;
    const h = rotated ? natural.w : natural.h;
    return Math.max(boxSize.w / w, boxSize.h / h);
  }, [natural, boxSize, rotated]);

  const scale = mode === 'fit' ? Math.min(fitScale, 1) : mode === 'fill' ? fillScale : mode === 'actual' ? 1 : custom;

  const zoomBy = useCallback((factor: number) => {
    setCustom(Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale * factor)));
    setMode('custom');
  }, [scale]);

  const reset = useCallback(() => {
    setMode('fit'); setRotation(0); setFlipX(false); setFlipY(false); setPan({ x: 0, y: 0 });
  }, []);

  const rotateBy = useCallback((deg: number) => {
    setRotation((r) => (r + deg + 360) % 360);
    setPan({ x: 0, y: 0 });
  }, []);

  useEffect(() => { setPan({ x: 0, y: 0 }); }, [mode]);

  const isSvg = detected.mime === 'image/svg+xml';
  const mayAnimate = /gif|apng|webp|avif/.test(detected.mime);

  // Draw the current view to a canvas, honouring rotation and flips, for Copy and Export.
  const snapshot = useCallback(async (): Promise<Blob | null> => {
    const el = img.current;
    if (!el || !natural) return null;
    const canvas = document.createElement('canvas');
    canvas.width = rotated ? natural.h : natural.w;
    canvas.height = rotated ? natural.w : natural.h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
    ctx.drawImage(el, -natural.w / 2, -natural.h / 2, natural.w, natural.h);
    return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
  }, [natural, rotated, rotation, flipX, flipY]);

  useEffect(() => {
    const canCopyImage = typeof ClipboardItem !== 'undefined' && typeof navigator !== 'undefined' && !!navigator.clipboard?.write;
    register({
      capabilities: { rotate: true, flip: true, copyImage: canCopyImage, exportPreview: true, fullscreen: true, print: true },
      actions: {
        copyImage: async () => {
          const blob = await snapshot();
          if (!blob) return false;
          try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            return true;
          } catch {
            return false;
          }
        },
        exportPreview: () => {
          void snapshot().then((blob) => {
            if (!blob) return;
            const base = file.name.replace(/\.[^.]+$/, '') || 'image';
            downloadBlob(blob, `${base}-preview.png`);
            announce(labels.exportPng);
          });
        },
        print: () => {
          if (!src) return;
          const w = window.open('', '_blank', 'noopener,width=900,height=700');
          if (!w) return;
          w.document.write(`<!doctype html><title>${escapeHtml(file.name)}</title><style>html,body{margin:0;height:100%}img{max-width:100%;max-height:100%;display:block;margin:auto}</style><img src="${src}" onload="window.print()">`);
          w.document.close();
        }
      },
      details: natural ? [
        { label: labels.detailDimensions, value: `${natural.w} × ${natural.h}`, copy: `${natural.w}x${natural.h}` },
        { label: labels.detailAspect, value: formatAspect(natural.w, natural.h) },
        { label: labels.detailRenderer, value: labels.rendererNative }
      ] : [],
      status: natural ? `${natural.w} × ${natural.h} · ${Math.round(scale * 100)}%` : undefined,
      onKey: (e) => {
        if (e.key === '+' || e.key === '=') { zoomBy(ZOOM_STEP); return true; }
        if (e.key === '-' || e.key === '_') { zoomBy(1 / ZOOM_STEP); return true; }
        if (e.key === '0') { setMode('fit'); return true; }
        if (e.key === '1') { setMode('actual'); return true; }
        if (e.key === 'r' || e.key === 'R') { rotateBy(e.shiftKey ? -90 : 90); return true; }
        return false;
      }
    });
  }, [register, natural, scale, snapshot, zoomBy, rotateBy, file.name, src, labels, announce]);

  const onWheel = (e: ReactWheelEvent<HTMLDivElement>) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? 1.1 : 1 / 1.1);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    setPan({ x: drag.current.px + (e.clientX - drag.current.x), y: drag.current.py + (e.clientY - drag.current.y) });
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    drag.current = null;
    try { (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId); } catch { /* not captured */ }
  };

  const transform = `translate(${pan.x}px, ${pan.y}px) rotate(${rotation}deg) scale(${(flipX ? -1 : 1) * scale}, ${(flipY ? -1 : 1) * scale})`;
  const modeValue = mode === 'custom' ? '' : mode;

  return (
    <div className="rr-fv__renderer">
      <Toolbar>
        <ToolbarGroup>
          <IconButton label={labels.zoomOut} onClick={() => zoomBy(1 / ZOOM_STEP)}><IconZoomOut /></IconButton>
          <ToolbarText mono>{Math.round(scale * 100)}%</ToolbarText>
          <IconButton label={labels.zoomIn} onClick={() => zoomBy(ZOOM_STEP)}><IconZoomIn /></IconButton>
        </ToolbarGroup>
        <ToolbarGroup>
          <SegmentedControl
            size="xs"
            value={modeValue}
            onChange={(v) => setMode((v || 'fit') as Mode)}
            data={[
              { label: labels.imageFit, value: 'fit' },
              { label: labels.imageFill, value: 'fill' },
              { label: labels.imageActual, value: 'actual' }
            ]}
          />
        </ToolbarGroup>
        <ToolbarGroup>
          <IconButton label={labels.rotateLeft} onClick={() => rotateBy(-90)}><IconRotateLeft /></IconButton>
          <IconButton label={labels.rotateRight} onClick={() => rotateBy(90)}><IconRotateRight /></IconButton>
          <IconButton label={labels.flipHorizontal} pressed={flipX} active={flipX} onClick={() => setFlipX((v) => !v)}><IconFlipH /></IconButton>
          <IconButton label={labels.flipVertical} pressed={flipY} active={flipY} onClick={() => setFlipY((v) => !v)}><IconFlipV /></IconButton>
          <IconButton label={labels.resetView} onClick={reset}><IconReset /></IconButton>
        </ToolbarGroup>
        <ToolbarGroup>
          <IconButton label={labels.checkerboard} pressed={checker} active={checker} onClick={() => setChecker((v) => !v)}><IconChecker /></IconButton>
          <ToolbarText>{labels.transformNote}</ToolbarText>
        </ToolbarGroup>
      </Toolbar>

      <div
        ref={box}
        className={['rr-fv__canvas', 'rr-fv__canvas--image', checker && 'rr-fv__canvas--checker', scale > fitScale && 'rr-fv__canvas--pannable'].filter(Boolean).join(' ')}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => setMode((m) => (m === 'actual' ? 'fit' : 'actual'))}
      >
        {failed ? (
          <div className="rr-fv__inline-error">{labels.errorDecode}</div>
        ) : (
          <img
            ref={img}
            className="rr-fv__image"
            src={src}
            alt={file.name}
            draggable={false}
            style={{
              transform,
              transition: reducedMotion || drag.current ? 'none' : 'transform 120ms ease-out',
              width: natural ? natural.w : undefined,
              height: natural ? natural.h : undefined
            }}
            onLoad={(e) => {
              const el = e.currentTarget;
              setNatural({ w: el.naturalWidth || el.width, h: el.naturalHeight || el.height });
            }}
            onError={() => setFailed(true)}
          />
        )}
      </div>
      {(isSvg || mayAnimate) && (
        <div className="rr-fv__canvas-note">{isSvg ? labels.svgNote : labels.animatedNote}</div>
      )}
    </div>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}
